import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="forgot-password-page">
      {/* Background */}
      <div className="absolute inset-0 bg-[#050a18]" />
      <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#0066CC]/15 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-15%] left-[-5%] w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="w-full max-w-[420px] px-6 relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/absolin-logo-hd.png" alt="ABSOLIN" className="h-10 w-auto mx-auto mb-2" data-testid="forgot-brand-logo" />
          <p className="text-white/30 text-[11px] tracking-[0.2em] uppercase">Infinite Digital Possibilities</p>
        </div>

        {/* Card */}
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 shadow-2xl">
          {sent ? (
            <div className="text-center py-2" data-testid="reset-email-sent">
              <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-white/50 text-sm mb-2">
                We sent a password reset link to <span className="text-white/80 font-medium">{email}</span>
              </p>
              <p className="text-white/30 text-xs mb-6">
                Link expires in 1 hour. Check spam if not found.
              </p>
              <div className="flex flex-col gap-2">
                <Button variant="outline" className="w-full bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white rounded-xl h-11" onClick={() => { setSent(false); setEmail(''); }} data-testid="try-again-btn">
                  Try another email
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full text-white/40 hover:text-white/70 hover:bg-transparent" data-testid="back-to-login-from-sent">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white">Forgot Password?</h2>
                <p className="text-white/40 text-sm mt-1">Enter your email to receive a reset link</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div className="space-y-1.5">
                  <Label className="text-white/50 text-xs font-semibold uppercase tracking-wider">Email Address</Label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-white/20 group-focus-within:text-[#0066CC] transition-colors" />
                    <Input
                      type="email" placeholder="you@company.com"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#0066CC]/50 focus:ring-2 focus:ring-[#0066CC]/10 rounded-xl"
                      required data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full h-12 bg-[#0066CC] hover:bg-[#0055aa] text-white font-semibold rounded-xl shadow-lg shadow-[#0066CC]/20" disabled={isLoading} data-testid="send-reset-link-btn">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center flex items-center justify-center gap-4">
          <Link to="/login" className="inline-flex items-center text-white/30 hover:text-white/60 text-sm transition-colors" data-testid="back-to-login-link">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Sign In
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/" className="inline-flex items-center text-white/30 hover:text-white/60 text-sm transition-colors" data-testid="back-to-website-link">
            Visit website
          </Link>
        </div>
      </div>
    </div>
  );
}
